import { useMediaQuery } from '@mui/material'
import { BigCalendar } from 'components/BigCalendar/BigCalendar'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { addDays, format, formatISO } from 'date-fns'
import {
  useCreateNewPickupSlot,
  useEditPickupSlot,
  useGetPickupSlot,
} from 'hooks/useServiceAreas'
import theme from 'lib/theme'
import { useAlert } from 'providers/AlertProvider'
import { useServiceAreas } from 'providers/ServiceAreasProvider'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  EditSlotModal,
  ValueProps as EditSlotValueProps,
} from './EditSlotModal'
import { NewSlotModal, ValueProps as NewSlotValueProps } from './NewSlotModal'
import { checkValidityStart, eventStyleGetter } from './utils'

export const PickupSlotCalendar = () => {
  const {
    dateData,
    setDateData,
    pickupSlots,
    selectedCollectionPoint,
    allDeliveryAreas,
    placeId,
    isLoadingPickupSlots,
  } = useServiceAreas()
  const { t } = useTranslation()
  const { setAlert } = useAlert()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { mutateAsync, isLoading } = useCreateNewPickupSlot(
    selectedCollectionPoint.id,
    dateData
  )
  const [slotId, setSlotId] = useState<string | undefined>(undefined)
  const [editSlotModal, setEditSlotModal] = useState({ right: false })
  const [newSlotStart, setNewSlotStart] = useState<Date | null>(null)
  const [newSlotEnd, setNewSlotEnd] = useState<Date | null>(null)
  const { isFetching, error, pickupSlot } = useGetPickupSlot(
    placeId,
    selectedCollectionPoint.id,
    slotId
  )
  const { mutateAsync: mutateEditSlot, isLoading: isLoadingEditRequest } =
    useEditPickupSlot(selectedCollectionPoint.id.toString(), dateData)
  const [modal, setModal] = useState({
    right: false,
  })

  const handleCloseModal = () => {
    setModal({ right: false })
    setEditSlotModal({ right: false })
  }

  const editPickupSlot = async (values: EditSlotValueProps) => {
    const deliverySlotData = {
      delivery_slots: [
        {
          id: slotId,
          pickup_location_id: selectedCollectionPoint.id,
          store_id: allDeliveryAreas[0].storeId,
          start_time: format(values.startTime, 'HH:mm'),
          end_time: format(values.endTime, 'HH:mm'),
          date: values.recurrent
            ? (Number(format(values.date, 'i')) - 1).toString()
            : formatISO(values.date, { representation: 'date' }),
          order_quota: Number(values.orderQuota) || 0,
          grace_hours: Number(values.graceHours),
          type: values.recurrent ? 'Recurrent' : 'Absolute',
          active: values.active,
          sequence_num: 0,
          delivery_price: Number(values.deliveryFee) || 0,
          collection_price: 0,
          delivery_type: 1,
          attributes: pickupSlot.attributes,
          ...(values.validityStart && {
            validity_start: formatISO(values.validityStart, {
              representation: 'date',
            }),
          }),
          ...(values.visibilityStart && {
            visibility_start: formatISO(values.visibilityStart, {
              representation: 'date',
            }),
          }),
          ...(values.visibilityEnd && {
            visibility_end: formatISO(values.visibilityEnd, {
              representation: 'date',
            }),
          }),
          ...(values.validityEnd && {
            validity_end: formatISO(values.validityEnd, {
              representation: 'date',
            }),
          }),
          ...(values.feeProducts && {
            master_product_id: values.feeProducts,
          }),
        },
      ],
    }
    await mutateEditSlot(deliverySlotData, {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
        handleCloseModal()
        setSlotId(undefined)
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'right',
        })
      },
    })
  }

  const createNewPickupSlot = async (values: NewSlotValueProps) => {
    const deliverySlotData = {
      delivery_slots: [
        {
          pickup_location_id: selectedCollectionPoint.id,
          store_id: allDeliveryAreas[0].storeId,
          start_time: format(values.startTime as Date, 'HH:mm'),
          end_time: format(values.endTime as Date, 'HH:mm'),
          date: values.recurrent
            ? (Number(format(values.date as Date, 'i')) - 1).toString()
            : formatISO(values.date as Date, { representation: 'date' }),
          order_quota: Number(values.orderQuota) || 0,
          grace_hours: Number(values.graceHours),
          type: values.recurrent ? 'Recurrent' : 'Absolute',
          active: values.active,
          sequence_num: 0,
          delivery_price: Number(values.deliveryFee) || 0,
          collection_price: 0,
          delivery_type: 1,
          ...(values.validityStart && {
            validity_start: formatISO(values.validityStart, {
              representation: 'date',
            }),
          }),
          ...(values.visibilityStart && {
            visibility_start: formatISO(values.visibilityStart, {
              representation: 'date',
            }),
          }),
          ...(values.visibilityEnd && {
            visibility_end: formatISO(values.visibilityEnd, {
              representation: 'date',
            }),
          }),
          ...(values.validityEnd && {
            validity_end: formatISO(values.validityEnd, {
              representation: 'date',
            }),
          }),
          ...(values.feeProducts && {
            product_id: values.feeProducts,
          }),
        },
      ],
    }
    await mutateAsync(deliverySlotData, {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
        handleCloseModal()
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'right',
        })
      },
    })
  }

  const handleSelectSlot = ({ start, end }: { start: Date; end: Date }) => {
    if (start > new Date()) {
      setNewSlotStart(start)
      setNewSlotEnd(end)
      setModal({ right: true })
    }
  }

  const handleSelectEvent = (e: {
    id: number
    end: Date
    start: Date
    title: string
  }) => {
    setSlotId(e.id.toString())
    setEditSlotModal({ right: true })
  }

  const pickupEvents =
    pickupSlots &&
    pickupSlots
      .filter((slot) =>
        checkValidityStart(slot.date, slot.validityStart, dateData.startOfWeek)
      )
      .map((slot) => {
        const {
          deliveryPrice,
          id,
          orderQuota,
          startTime,
          endTime,
          type,
          date,
        } = slot
        const feeProductName =
          slot.product && slot.product.extraProduct === 'FEE'
            ? slot.product.name
            : null
        const formattedDate =
          date.length > 1
            ? date
            : format(
                addDays(new Date(dateData.startOfWeek), Number(date)),
                'yyyy-MM-dd'
              )
        return {
          id,
          resource: type,
          title: feeProductName
            ? `${feeProductName} \n${orderQuota}`
            : `${parseFloat(deliveryPrice).toFixed(2)}\n${orderQuota}`,
          start: new Date(`${formattedDate}T${startTime}`),
          end: new Date(
            `${formattedDate}T${endTime === '00:00' ? '23:59' : endTime}`
          ),
        }
      })
  if (isLoadingPickupSlots) {
    return <LoadingIndicator />
  }
  if (error) return <h1>Error</h1>
  return (
    <>
      <BigCalendar
        onSelectEvent={handleSelectEvent}
        handleSelectSlot={handleSelectSlot}
        events={pickupEvents}
        dateData={dateData}
        setDateData={setDateData}
        eventStyleGetter={eventStyleGetter}
      />
      <NewSlotModal
        isLoading={isLoading}
        startTime={newSlotStart}
        endTime={newSlotEnd}
        modal={modal}
        handleCloseModal={handleCloseModal}
        handleSubmit={createNewPickupSlot}
      />
      {pickupSlot && pickupSlot.id && (
        <EditSlotModal
          isFetching={isFetching}
          slotDetails={pickupSlot}
          isLoading={isLoadingEditRequest}
          modal={editSlotModal}
          handleCloseModal={handleCloseModal}
          handleSubmit={editPickupSlot}
          handleDuplicate={createNewPickupSlot}
          isLoadingDuplicate={isLoading}
        />
      )}
    </>
  )
}
